/**
 * Reading the workspace document HONESTLY.
 *
 * The hub mirrors the Team directory into the server's access map
 * (`POST /auth/workspace-memberships/sync`). That call rewrites who can sign in
 * to the workspace, so the one thing it must never carry is a list the hub did
 * not actually read. It used to: `GET /api/workspace/doc` answered `ok:true`
 * with `{}` when the read threw, the hub normalised that to `teamMembers: []`,
 * and posted it on every bootstrap -- so one database blip while an owner
 * opened the hub deleted that workspace's whole access map, and since a user
 * with no membership row then read as the owner of their own workspace, it
 * promoted every teammate permanently.
 *
 * These two functions are that rule, on their own, with no I/O and nothing that
 * throws: every unreadable input answers "do not mirror".
 */

/**
 * The team list EXACTLY as the stored document carried it, or `null` when the
 * document did not carry one at all.
 *
 * Deliberately NOT the normalised `teamMembers`, which answers `[]` for a
 * document with no `teamMembers` key, for a document that failed to load and
 * for a genuinely empty directory alike. Those are three different answers and
 * only one of them means "this workspace has nobody in it".
 */
export function storedTeamList(doc) {
  if (!doc || typeof doc !== "object" || Array.isArray(doc)) return null;
  // Same shape rule the hub's normalizeRemoteDoc uses: settings may be nested
  // under `settings` or sit flat on the document.
  let settings = doc;
  try {
    if (
      doc.settings &&
      typeof doc.settings === "object" &&
      !Array.isArray(doc.settings)
    ) {
      settings = doc.settings;
    }
    if (!Object.prototype.hasOwnProperty.call(settings, "teamMembers")) {
      return null;
    }
    return Array.isArray(settings.teamMembers) ? settings.teamMembers : null;
  } catch {
    // A hostile or exotic object (a throwing getter). Unreadable is "no list".
    return null;
  }
}

// The server's own address rule (workspaceDirectoryPolicy.js EMAIL_SHAPE).
const EMAIL_SHAPE = /^[^@\s]+@[^@\s]+$/;

/**
 * The directory addresses the SERVER would turn into access-map rows, in
 * order: exactly `normalizeSyncPayload`'s rule in
 * backend/server/utils/workspaceDirectoryPolicy.js. Only `member.email` (never
 * aliases), trimmed and lowercased, email-shaped, not the workspace owner's own
 * address, first occurrence of each.
 *
 * Why the hub needs it: the server refuses an ordinary sync that yields ZERO
 * rows (`directory_sync_empty_refused`), and a directory holding only the
 * owner's own card, or rows with no usable address, yields zero rows while
 * still LOOKING non-empty. Deciding "is this directory empty?" by
 * `members.length` sent such a list as an ordinary save, the server refused it,
 * and the last real teammate kept their access.
 *
 * @param {Array} members Team directory rows
 * @param {string} ownerEmail the workspace owner's address
 * @returns {string[]} never throws; anything unreadable contributes nothing
 */
export function writableDirectoryEmails(members, ownerEmail) {
  const out = [];
  if (!Array.isArray(members)) return out;
  let owner = "";
  try {
    owner = String(ownerEmail == null ? "" : ownerEmail).trim().toLowerCase();
  } catch {
    owner = "";
  }
  const seen = new Set();
  for (const member of members) {
    let address = "";
    try {
      const row = member && typeof member === "object" ? member : {};
      address = String(row.email == null ? "" : row.email).trim().toLowerCase();
    } catch {
      continue;
    }
    if (!address || !EMAIL_SHAPE.test(address)) continue;
    if (owner && address === owner) continue;
    if (seen.has(address)) continue;
    seen.add(address);
    out.push(address);
  }
  return out;
}

/** Is this an address the server would accept as a directory row at all? */
export function isDirectoryAddress(value) {
  try {
    const address = String(value == null ? "" : value).trim().toLowerCase();
    return !!address && EMAIL_SHAPE.test(address);
  } catch {
    return false;
  }
}

/** How many access-map rows the server would write for this directory. */
export function countWritableDirectoryRows(members, ownerEmail) {
  return writableDirectoryEmails(members, ownerEmail).length;
}

/**
 * May this page mirror the directory into the access map right now?
 *
 * TRUE needs all five, and each one is a separate way the old code got it
 * wrong:
 *  - `docPresent === true`   the server affirmatively said this workspace HAS a
 *                            stored document. A failed read never gets here (it
 *                            throws), and an empty workspace says false.
 *  - a stored list            the document itself carried `teamMembers`. A
 *                            document with no such key is an unanswered
 *                            question, not an empty directory.
 *  - a WRITABLE row to send  the server refuses a payload that yields no rows;
 *                            emptying a directory on purpose is a separate,
 *                            explicit intent (`clearDirectory: true`) that no
 *                            automatic save may reach for. Counted with the
 *                            server's rule (`countWritableDirectoryRows`), so
 *                            a directory holding only the owner's own card or
 *                            rows with no usable address does not fire a
 *                            refused mirror (and an audit row) on every load.
 *  - `isOwner`               the route is owner-only. An admin would get a 403
 *                            and a failed security event on every page load.
 *  - an `ownerEmail`         without it the owner's own card cannot be told
 *                            apart from a teammate, so the count above is not
 *                            the server's count.
 */
export function shouldMirrorTeamDirectory({
  docPresent,
  doc,
  members,
  isOwner,
  ownerEmail,
} = {}) {
  if (docPresent !== true) return false;
  if (isOwner !== true) return false;
  let owner = "";
  try {
    owner = String(ownerEmail == null ? "" : ownerEmail).trim().toLowerCase();
  } catch {
    return false;
  }
  if (!EMAIL_SHAPE.test(owner)) return false;
  const stored = storedTeamList(doc);
  if (!Array.isArray(stored) || stored.length === 0) return false;
  return countWritableDirectoryRows(members, owner) > 0;
}

/**
 * Turn a refusal from `POST /auth/workspace-memberships/sync` into a sentence
 * the workspace owner can act on.
 *
 * WHY THIS EXISTS: both call sites used to be `.catch(() => {})`. The server
 * grew seven named refusals that each carry the offending address, several of
 * which a perfectly ordinary owner can hit on an ordinary save, and every one of
 * them was thrown on the floor — so a save the server REFUSED looked exactly
 * like one that worked. `PUT /api/workspace/doc` has already stored the new
 * list by then, so the document said one thing and the access map said another,
 * with nothing on screen to show it. That is the divergence this whole change
 * set exists to remove, arriving on the owner's own path.
 *
 * Every message names what was NOT changed, because that is the fact the owner
 * needs: the directory on screen saved, the ACCESS did not.
 *
 * `scope: "doc"` is the other refusal: `PUT /api/workspace/doc` itself refused
 * an address the save ADDED to the directory, so NOTHING saved, not the
 * directory and not any other setting on the page. Every later save carries
 * the same address and is refused the same way until the owner removes it, so
 * those messages say that and name the address.
 *
 * @param {object} [input]
 * @param {string} [input.error] the server's `error` code
 * @param {string} [input.email] the offending address, when the code carries one
 * @param {number} [input.status] the HTTP status, for codes with no message
 * @param {string} [input.scope] "doc" when the document save itself was refused
 * @returns {string} one sentence, never empty
 */
export function describeDirectorySyncError(input) {
  let code = "";
  let email = "";
  let status = 0;
  let scope = "";
  try {
    const source = input && typeof input === "object" ? input : {};
    code = String(source.error == null ? "" : source.error).trim();
    email = String(source.email == null ? "" : source.email).trim();
    status = Number(source.status) || 0;
    scope = String(source.scope == null ? "" : source.scope).trim();
  } catch {
    return "Workspace access could not be updated, so the Team directory on screen may not match who can actually sign in.";
  }
  const who = email || "That address";
  if (scope === "doc") {
    switch (code) {
      case "directory_sync_member_other_workspace":
        return `${who} already belongs to a different BossDrop workspace, so none of your changes were saved. Remove that address from the Team directory to save again.`;
      case "directory_sync_member_is_owner":
        return `${who} owns a BossDrop workspace of their own and cannot also be a teammate here, so none of your changes were saved. Remove that address from the Team directory to save again.`;
      case "directory_sync_member_protected":
        return `${who} is a BossDrop platform address and cannot be listed in a Team directory, so none of your changes were saved. Remove it from the Team directory to save again.`;
      case "rate_limited":
        return "You added a lot of new addresses to the Team directory in a short time, so BossDrop paused for a while and none of your changes were saved. Wait a bit, then save again.";
      default:
        return `BossDrop refused the Team directory${code ? ` (${code})` : ""}, so none of your changes were saved. Check the newest addresses in the directory and save again.`;
    }
  }
  switch (code) {
    case "directory_sync_member_other_workspace":
      return `${who} already belongs to a different BossDrop workspace, so workspace access was not updated. Remove that address from the directory and save again.`;
    case "directory_sync_member_is_owner":
      return `${who} owns a BossDrop workspace of their own, so they cannot also be a teammate here and workspace access was not updated. Remove that address from the directory and save again.`;
    case "directory_sync_member_protected":
      return `${who} is a BossDrop platform address and cannot be listed in a Team directory, so workspace access was not updated. Remove it and save again.`;
    case "directory_sync_empty_refused":
      return "Workspace access was not updated: the Team directory has no teammate address BossDrop can apply (your own address and incomplete addresses do not count), and an empty directory is only accepted as a deliberate \u201cremove everyone\u201d.";
    case "directory_sync_caller_removed":
      return "Workspace access was not updated: that save would have removed your own access to this workspace.";
    case "directory_sync_caller_escalated":
      return "Workspace access was not updated: that save would have raised your own access level.";
    case "directory_sync_conflict":
      return "Someone else was saving this workspace\u2019s Team directory at the same moment, so nothing was changed. Try saving again.";
    case "directory_sync_owner_missing":
    case "directory_sync_payload_invalid":
      return "BossDrop could not read the Team directory it was sent, so workspace access was not updated.";
    case "workspace_owner_only":
      return "Only the workspace owner can change the Team directory, so workspace access was not updated.";
    case "workspace_sync_failed":
      return "BossDrop could not update workspace access just now, so the Team directory on screen may not match who can actually sign in. Try again.";
    case "rate_limited":
      return "You added a lot of new addresses to the Team directory in a short time, so BossDrop paused and workspace access was not updated. Wait a bit, then save again.";
    default:
      break;
  }
  if (status === 403) {
    return "Only the workspace owner can change the Team directory, so workspace access was not updated.";
  }
  return `Workspace access could not be updated${code ? ` (${code})` : ""}, so the Team directory on screen may not match who can actually sign in.`;
}

/**
 * Read a sync refusal out of whatever the fetch helper threw.
 *
 * `helpers/auth.js` throws `Error("http_error")` carrying `.status` and the
 * parsed body on `.data`; a network failure throws something with neither. Both
 * must end up as a VISIBLE error, so an unreadable rejection still answers a
 * code rather than nothing.
 */
export function readDirectorySyncFailure(error) {
  try {
    const data = error && typeof error === "object" ? error.data : null;
    return {
      error: String(data?.error || "") || "workspace_sync_failed",
      email: String(data?.email || ""),
      status: Number(error?.status) || 0,
    };
  } catch {
    return { error: "workspace_sync_failed", email: "", status: 0 };
  }
}
